import type { ReactElement } from 'react';
import { useMemo, useState } from 'react';

import type { AiModelSettings, AssessDeckPowerInput, DeckPowerAssessment } from '@mtgcraft/shared';
import { AI_FEATURES } from '@mtgcraft/shared';

import { exportDeckForAi } from '@/features/import-export/exporters/ai';
import type { Deck, EnrichedDeckCard } from '@/types/deck';

import AiFeatureModal from '../components/AiFeatureModal';
import useAiFeature from '../hooks/useAiFeature';
import DeckPowerResult from './DeckPowerResult';

const META = AI_FEATURES['deck-power-assessment'];

export interface DeckPowerModalProps {
  isOpen: boolean;
  onClose: () => void;
  deck: Deck;
  cards: EnrichedDeckCard[];
}

export default function DeckPowerModal({ isOpen, onClose, deck, cards }: DeckPowerModalProps): ReactElement {
  const [includeSideboard, setIncludeSideboard] = useState(true);
  const [includeOracleText, setIncludeOracleText] = useState(true);

  const { execute, isRunning, elapsedMs, run, error } = useAiFeature<AssessDeckPowerInput, DeckPowerAssessment>('assessDeckPower');

  const hasSideboard = cards.some((card) => card.zone === 'sideboard');

  const { deckText, oracleDropped } = useMemo(() => {
    // Maybeboard, price, ownership and popularity say nothing about current power and bias the rating.
    const build = (withOracleText: boolean) =>
      exportDeckForAi(deck, cards, {
        verbose: true,
        includeMaybeboard: false,
        includePrices: false,
        includeOwnership: false,
        includeEdhrecRank: false,
        includeSideboard: hasSideboard && includeSideboard,
        includeOracleText: withOracleText,
      });

    const text = build(includeOracleText);

    // Oracle text roughly triples the payload; drop it rather than block a big deck outright.
    if (includeOracleText && text.length > META.maxInputChars) {
      return { deckText: build(false), oracleDropped: true };
    }

    return { deckText: text, oracleDropped: false };
  }, [deck, cards, hasSideboard, includeSideboard, includeOracleText]);

  const handleRun = (settings: AiModelSettings | null) => {
    execute({
      deckId: deck.id,
      deckText,
      format: deck.format,
      ...(settings ? { settings } : {}),
    });
  };

  // Falls back to the stored assessment so reopening the modal shows the last result.
  const stored = deck.ai_assessment ?? null;
  const displayed = run?.result ?? stored;
  const isStored = !run?.result && stored !== null;

  return (
    <AiFeatureModal
      isOpen={isOpen}
      onClose={onClose}
      title={META.label}
      description={META.description}
      feature={META.id}
      inputPreview={deckText}
      maxInputChars={META.maxInputChars}
      isRunning={isRunning}
      elapsedMs={elapsedMs}
      error={error}
      run={run}
      onRun={handleRun}
      runLabel="Assess deck"
      disabledReason={cards.length === 0 ? 'Add cards to the deck before assessing it.' : null}
      result={
        displayed ? (
          <DeckPowerResult
            assessment={displayed}
            savedAt={isStored ? stored?.created_at : null}
            savedModel={isStored ? stored?.model : null}
          />
        ) : null
      }
      options={
        <fieldset className="space-y-2">
          <legend className="text-ash mb-2 text-xs font-medium tracking-wide uppercase">What to send</legend>

          {hasSideboard && (
            <label className="text-silver flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={includeSideboard}
                onChange={(event) => setIncludeSideboard(event.target.checked)}
                className="accent-aether focus-ring h-4 w-4 rounded"
              />
              Include sideboard
            </label>
          )}

          <label className="text-silver flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={includeOracleText}
              onChange={(event) => setIncludeOracleText(event.target.checked)}
              className="accent-aether focus-ring h-4 w-4 rounded"
            />
            Include oracle text
            <span className="text-ash text-xs">— more accurate, much larger</span>
          </label>

          {oracleDropped && <p className="text-gold text-xs">Oracle text dropped — the deck exceeds the size limit with it.</p>}
        </fieldset>
      }
    />
  );
}
